import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import parseGeoraster from 'georaster';
import GeoRasterLayer from 'georaster-layer-for-leaflet';
import { ruptureUrl, contourUrl, waveheightUrl } from '../utils/layerLoader';
import apiFetch from '../utils/apiFetch';
import AOILayer from './AOILayer';

// Fix broken default marker icons when bundled with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function MapClickHandler({ onMapClick }) {
  useMapEvents({ click: e => onMapClick(e.latlng.lat, e.latlng.lng) });
  return null;
}

// ── Map controls (zoom position, north arrow, locate, scale) ─────────────────

function MapControls() {
  const map = useMap();

  useEffect(() => {
    // Scale bar added FIRST so it lands at the very bottom of the bottom-right stack.
    // Leaflet stacks bottomright controls with first-added at the bottom.
    const scale = L.control.scale({
      position: 'bottomright',
      metric: true,
      imperial: false,
      maxWidth: 90,
    }).addTo(map);

    // Zoom, Locate, North added after scale → stack above it (visible: North → Locate → Zoom → Scale)
    const zoom = L.control.zoom({ position: 'bottomright' }).addTo(map);

    // Locate button — added before North so it sits below North in the stack
    let locationMarker = null;
    const LocateControl = L.Control.extend({
      options: { position: 'bottomright' },
      onAdd() {
        const btn = L.DomUtil.create('button', 'locate-btn');
        btn.type = 'button';
        btn.title = 'Go to your location';
        btn.setAttribute('aria-label', 'Go to your location');
        btn.innerHTML =
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
            '<circle cx="12" cy="12" r="3.4"/>' +
            '<path d="M12 2.5v3.2M12 18.3v3.2M2.5 12h3.2M18.3 12h3.2" stroke-linecap="round"/>' +
          '</svg>';
        L.DomEvent.disableClickPropagation(btn);
        L.DomEvent.on(btn, 'click', e => {
          L.DomEvent.stop(e);
          if (!navigator.geolocation) {
            btn.title = 'Geolocation not supported';
            return;
          }
          btn.classList.add('locating');
          navigator.geolocation.getCurrentPosition(
            ({ coords: { latitude: lat, longitude: lon } }) => {
              btn.classList.remove('locating');
              if (locationMarker) map.removeLayer(locationMarker);
              const userLocIcon = L.divIcon({
                className: '',
                html: '<div class="userloc"><div class="userloc-halo"></div><div class="userloc-core"></div></div>',
                iconSize: [0, 0],
                iconAnchor: [0, 0],
              });
              locationMarker = L.marker([lat, lon], { icon: userLocIcon })
                .addTo(map)
                .bindTooltip('Your location');
              map.flyTo([lat, lon], 8, { duration: 1 });
            },
            () => {
              btn.classList.remove('locating');
              const orig = btn.title;
              btn.title = 'Location access denied';
              setTimeout(() => { btn.title = orig; }, 3000);
            },
            { enableHighAccuracy: true, timeout: 8000 }
          );
        });
        return btn;
      },
    });
    const locateControl = new LocateControl().addTo(map);

    // North indicator — added last so it sits at the top of the stack
    const NorthArrow = L.Control.extend({
      options: { position: 'bottomright' },
      onAdd() {
        const el = L.DomUtil.create('div', 'map-ctrl-north');
        el.title = 'North is up';
        el.innerHTML =
          '<span class="north-arrow-icon">▲</span>' +
          '<span class="north-arrow-label">N</span>';
        L.DomEvent.disableClickPropagation(el);
        return el;
      },
    });
    const northControl = new NorthArrow().addTo(map);

    return () => {
      scale.remove();
      zoom.remove();
      locateControl.remove();
      northControl.remove();
      if (locationMarker) { map.removeLayer(locationMarker); locationMarker = null; }
    };
  }, [map]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

// ── Wave height ──────────────────────────────────────────────────────────────

// Color classification for wave height (meters).
// ESRI FLT_MAX sentinel (>1e10) and negative values (-9999 or noise) → transparent.
// Zero (dry land / no inundation) → transparent.
function pixelValuesToColorFn(values) {
  const v = values[0];
  if (v === undefined || v === null) return null;
  if (v > 1e10) return null;  // ESRI FLT_MAX no-data sentinel
  if (v <= 0)   return null;  // negative sentinel (-9999) and zero → transparent
  if (v < 0.2)  return '#CCFFFF';  // < 0.2 m   light cyan
  if (v < 1.0)  return '#FFFFCC';  // 0.2–1 m   cream
  if (v < 3.0)  return '#FFCC00';  // 1–3 m     yellow
  if (v < 5.0)  return '#FF8C00';  // 3–5 m     orange
  if (v < 10.0) return '#FF2200';  // 5–10 m    red
  return '#8B0000';                 // > 10 m    dark red
}

// Scan the raster band once and return the maximum valid wave-height value.
// Skips no-data cells (null, <= 0) and the ESRI FLT_MAX sentinel (> 1e10).
function rasterMax(georaster) {
  const band = georaster.values[0];
  let max = 0;
  for (let r = 0; r < band.length; r++) {
    const row = band[r];
    for (let c = 0; c < row.length; c++) {
      const v = row[c];
      if (v == null || v <= 0 || v > 1e10) continue;
      if (v > max) max = v;
    }
  }
  return max > 0 ? max : null;
}

function WaveheightLayer({ scenarioId, visible, onMaxCoastal }) {
  const map = useMap();
  const layerRef = useRef(null);
  const visibleRef = useRef(visible);

  visibleRef.current = visible;

  // Effect 1 – load a new georaster whenever the scenario changes
  useEffect(() => {
    console.log('[WaveHeight] scenarioId effect →', scenarioId);

    if (layerRef.current) {
      console.log('[WaveHeight] removing old layer');
      map.removeLayer(layerRef.current);
      layerRef.current = null;
    }

    if (!scenarioId) return;

    let cancelled = false;
    const url = waveheightUrl(scenarioId);
    console.log('[WaveHeight] fetching:', url);

    apiFetch(url)
      .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.arrayBuffer(); })
      .then(buf => parseGeoraster(buf))
      .then(georaster => {
        if (cancelled) { console.log('[WaveHeight] fetch cancelled'); return; }
        console.log('[WaveHeight] georaster ready:', georaster);
        if (onMaxCoastal) onMaxCoastal(rasterMax(georaster));
        const layer = new GeoRasterLayer({
          georaster,
          opacity: 1,
          pixelValuesToColorFn,
          resolution: 256,
        });
        layerRef.current = layer;
        if (visibleRef.current) {
          map.addLayer(layer);
          console.log('[WaveHeight] layer added to map');
        } else {
          console.log('[WaveHeight] layer cached (invisible)');
        }
      })
      .catch(err => console.error('[WaveHeight] load error:', err));

    return () => { cancelled = true; };
  }, [scenarioId, map]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect 2 – toggle visibility without re-fetching
  useEffect(() => {
    console.log('[WaveHeight] toggle effect → visible =', visible, 'hasLayer =', !!layerRef.current);
    if (!layerRef.current) return;
    if (visible) {
      map.addLayer(layerRef.current);
      console.log('[WaveHeight] layer shown');
    } else {
      map.removeLayer(layerRef.current);
      console.log('[WaveHeight] layer hidden');
    }
  }, [visible, map]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

// ── Arrival time contours ────────────────────────────────────────────────────

// Major contour: Contour value divisible by 30 → dark blue, labelled
// Minor contour: otherwise → light gray, no label
const contourStyle = feature => {
  const isMajor = feature.properties.Contour % 30 === 0;
  return {
    color: isMajor ? '#000078' : '#E1E1E1',
    weight: isMajor ? 1.25 : 1,
    fillOpacity: 0,
  };
};

const onEachContour = (feature, layer) => {
  const val = feature.properties.Contour;
  if (val % 30 === 0) {
    layer.bindTooltip(`${val} min`, {
      permanent: true,
      direction: 'center',
      className: 'contour-label',
      pane: 'labelsPane',
    });
  }
};

function ContourLayer({ data, visible }) {
  const map = useMap();
  const layerRef = useRef(null);
  const visibleRef = useRef(visible);

  visibleRef.current = visible;

  // Effect 1 – (re)create the GeoJSON layer when data changes
  useEffect(() => {
    console.log('[Contour] data effect → hasData =', !!data, 'visible =', visibleRef.current);

    if (layerRef.current) {
      console.log('[Contour] removing old layer');
      map.removeLayer(layerRef.current);
      layerRef.current = null;
    }

    if (!data) return;

    const layer = L.geoJSON(data, { style: contourStyle, onEachFeature: onEachContour });
    layerRef.current = layer;

    if (visibleRef.current) {
      map.addLayer(layer);
      console.log('[Contour] layer added to map');
    } else {
      console.log('[Contour] layer cached (invisible)');
    }

    return () => {
      // Clean up when data changes — the next run handles actual removal above
    };
  }, [data, map]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect 2 – toggle without re-creating
  useEffect(() => {
    console.log('[Contour] toggle effect → visible =', visible, 'hasLayer =', !!layerRef.current);
    if (!layerRef.current) return;
    if (visible) {
      map.addLayer(layerRef.current);
      console.log('[Contour] layer shown');
    } else {
      map.removeLayer(layerRef.current);
      console.log('[Contour] layer hidden');
    }
  }, [visible, map]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

// ── Rupture zone ─────────────────────────────────────────────────────────────

function RuptureLayer({ data, visible }) {
  const map = useMap();
  const layerRef = useRef(null);
  const visibleRef = useRef(visible);

  visibleRef.current = visible;

  // Effect 1 – (re)create the GeoJSON layer when data changes
  useEffect(() => {
    console.log('[Rupture] data effect → hasData =', !!data, 'visible =', visibleRef.current);

    if (layerRef.current) {
      console.log('[Rupture] removing old layer');
      map.removeLayer(layerRef.current);
      layerRef.current = null;
    }

    if (!data) return;

    const layer = L.geoJSON(data, {
      //style: { color: '#e74c3c', weight: 1, fillColor: '#e74c3c', fillOpacity: 0.35 },
      style: { color: '#e74c3c', weight: 0, fillColor: '#e74c3c', fillOpacity: 0 },
    });
    layerRef.current = layer;

    if (visibleRef.current) {
      map.addLayer(layer);
      console.log('[Rupture] layer added to map');
    } else {
      console.log('[Rupture] layer cached (invisible)');
    }

    return () => {
      // Next run of this effect handles removal above
    };
  }, [data, map]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect 2 – toggle without re-creating
  useEffect(() => {
    console.log('[Rupture] toggle effect → visible =', visible, 'hasLayer =', !!layerRef.current);
    if (!layerRef.current) return;
    if (visible) {
      map.addLayer(layerRef.current);
      console.log('[Rupture] layer shown');
    } else {
      map.removeLayer(layerRef.current);
      console.log('[Rupture] layer hidden');
    }
  }, [visible, map]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

// ── Search pin ───────────────────────────────────────────────────────────────

function SearchPinLayer({ pin }) {
  const map = useMap();
  const markerRef = useRef(null);

  useEffect(() => {
    if (markerRef.current) {
      map.removeLayer(markerRef.current);
      markerRef.current = null;
    }
    if (!pin) return;

    const epiIcon = L.divIcon({
      className: '',
      html: '<div class="epi-marker"><div class="epi-core"></div><div class="epi-pulse"></div></div>',
      iconSize: [0, 0],
      iconAnchor: [0, 0],
    });
    const marker = L.marker([pin.lat, pin.lng], { icon: epiIcon });
    // Use custom popup HTML when provided (e.g. earthquake events),
    // otherwise fall back to a simple lat/lng label.
    marker.bindPopup(
      pin.popup || `Lat: ${pin.lat.toFixed(4)}&nbsp;&nbsp;Lng: ${pin.lng.toFixed(4)}`
    );
    marker.addTo(map);
    markerRef.current = marker;
    map.flyTo([pin.lat, pin.lng], 6);
  }, [pin, map]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (markerRef.current) {
        map.removeLayer(markerRef.current);
        markerRef.current = null;
      }
    };
  }, [map]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

// ── Map component ────────────────────────────────────────────────────────────

export default function MapView({
  magnitude,
  selectedScenario,
  layerVisibility,
  onMapClick,
  hasClicked,
  searchPin,
  onMaxCoastal,
}) {
  const [selectedRuptureData, setSelectedRuptureData] = useState(null);
  const [contourData, setContourData] = useState(null);

  useEffect(() => {
    setSelectedRuptureData(null);
    if (!selectedScenario) return;
    console.log('[MapView] fetching rupture for', selectedScenario);
    apiFetch(ruptureUrl(selectedScenario))
      .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
      .then(data => { console.log('[MapView] rupture data loaded'); setSelectedRuptureData(data); })
      .catch(err => console.error('[MapView] rupture fetch error:', err));
  }, [selectedScenario]);

  useEffect(() => {
    setContourData(null);
    if (!selectedScenario) return;
    console.log('[MapView] fetching contour for', selectedScenario);
    apiFetch(contourUrl(selectedScenario))
      .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
      .then(data => { console.log('[MapView] contour data loaded'); setContourData(data); })
      .catch(err => console.error('[MapView] contour fetch error:', err));
  }, [selectedScenario]);

  return (
    <MapContainer
      center={[10, 96]}
      zoom={5}
      zoomControl={false}
      style={{ width: '100%', height: '100%' }}
      className={magnitude && !hasClicked ? 'cursor-crosshair' : ''}
    >
      <TileLayer
        url="https://tiles.stadiamaps.com/tiles/outdoors/{z}/{x}/{y}@2x.png?api_key=64664055-cfa5-4d63-8f09-6dc0a73292b3"
        attribution='&copy; <a href="https://www.stadiamaps.com/" target="_blank">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      />

      <MapClickHandler onMapClick={onMapClick} />
      <MapControls />

      <RuptureLayer data={selectedRuptureData} visible={layerVisibility.rupture} />
      <ContourLayer data={contourData}            visible={layerVisibility.contour} />
      <WaveheightLayer scenarioId={selectedScenario} visible={layerVisibility.waveheight} onMaxCoastal={onMaxCoastal} />
      <AOILayer        scenarioId={selectedScenario} visible={layerVisibility.waveheight} />
      <SearchPinLayer pin={searchPin} />
    </MapContainer>
  );
}
